/*
 * predictive-search-enhance
 *
 * Two additions to the theme's own predictive search:
 *
 *   1. Names the resource types explicitly, so articles and pages appear
 *      alongside products and collections rather than being left to the
 *      endpoint's default.
 *
 *   2. Renders a live filtered suggestions column, the equivalent of the
 *      "Populära förslag" list the old search app showed.
 *
 * Why the suggestions are built here rather than taken from Shopify: the
 * predictive search endpoint does return query suggestions, but only for stores
 * whose primary language AND buyer session are English. This storefront is
 * Swedish, so predictive_search.resources.queries is always empty and the
 * theme's own markup for that column never renders. The list is therefore built
 * from the "Most searched keywords" theme setting and filtered as the customer
 * types, which is the same substring behaviour the old app had.
 *
 * Kept in its own asset file and applied by patching the prototype rather than
 * editing search.js, because a search-backup.js already sits alongside it in
 * assets, which means Foxecom theme updates overwrite that file.
 */
(function () {
  var RESOURCE_TYPES = 'product,collection,page,article';
  var MAX_SUGGESTIONS = 5;

  /*
   * Shopify's predictive endpoint searches only title, product_type,
   * variants.title and vendor by default, while the full results page also
   * searches descriptions and tags. That asymmetry is why a query like
   * "klinker badrum" returned nothing in the dropdown while the results page
   * returned hundreds: no product has "badrum" in its title, but plenty mention
   * it in the description. Naming the fields explicitly makes the two agree.
   *
   * Shopify recommends the default set for precision, and body does pull in
   * looser matches, but a dropdown that contradicts its own results page is
   * worse. Drop 'body' from this list to trade recall back for precision.
   */
  var SEARCH_FIELDS = 'title,product_type,variants.title,vendor,tag,body';

  /* ------------------------------------------------------------------ *
   * Product type narrowing
   *
   * A search like "klinker badrum" should return klinker, with badrum acting
   * as the use case, rather than mixing in bathroom furniture that happens to
   * mention badrum in its description.
   *
   * This is done by adding the product type FILTER to the search URL, not by
   * rewriting the query. Shopify switches off product boosts, synonyms, typo
   * tolerance and semantic matching the moment a query contains search syntax
   * such as "product_type:Klinker AND badrum", whereas a filter leaves all of
   * that working and makes Shopify rebuild the other filters from the narrowed
   * set, which is what clears the furniture widths out of Mått.
   *
   * The values on the right must match the Product type values in the admin
   * exactly. The words on the left are what customers actually type, including
   * plurals and compounds, since "klinkers" appears in KakelExpress's own page
   * titles and would otherwise narrow nothing.
   * ------------------------------------------------------------------ */
  var TYPE_WORDS = {
    klinker: 'Klinker',
    klinkers: 'Klinker',
    golvklinker: 'Klinker',
    vaggklinker: 'Klinker',
    utomhusklinker: 'Klinker',
    granitklinker: 'Klinker',
    traklinker: 'Klinker',
    kakel: 'Kakel',
    vaggkakel: 'Kakel',
    kakelplattor: 'Kakel',
    mosaik: 'Mosaik',
    glasmosaik: 'Mosaik',
    vinylgolv: 'Vinylgolv',
    vinyl: 'Vinylgolv',
    tragolv: 'Trägolv',
    parkett: 'Trägolv',
  };

  var TYPE_PARAM = 'filter.p.product_type';

  /* ------------------------------------------------------------------ *
   * Suggestions
   * ------------------------------------------------------------------ */

  function getKeywords(el) {
    var raw = el.dataset.searchKeywords || '';
    return raw
      .split(',')
      .map(function (word) {
        return word.trim();
      })
      .filter(function (word) {
        return word.length > 0;
      });
  }

  /*
   * Matches the way the old app behaved: a plain substring test, not a prefix
   * one. Typing "a" surfaced "verde alpi" there because of the a in "alpi".
   * Comparison is lowercased and accent aware via localeCompare-friendly
   * normalisation, so "aspo" still finds "aspö".
   */
  function normalise(text) {
    var lowered = String(text).toLowerCase();
    return lowered.normalize ? lowered.normalize('NFD').replace(/[\u0300-\u036f]/g, '') : lowered;
  }

  function matchIndex(keyword, query) {
    return normalise(keyword).indexOf(normalise(query));
  }

  /*
   * Returns the product type to narrow to, or null.
   *
   * Null when nothing matches, and deliberately null when the query names two
   * types: someone searching "kakel klinker" is comparing, and silently hiding
   * half of what they asked for is worse than not narrowing at all.
   *
   * Matching is on whole words against the accent-folded query, so "tragolv"
   * finds trägolv and a product called "Klinkerkross" does not trigger on a
   * search for something else.
   */
  function detectProductType(query) {
    if (!query) return null;

    var tokens = normalise(query).split(/[^a-z0-9]+/);
    var found = [];

    tokens.forEach(function (token) {
      var type = TYPE_WORDS[token];
      if (type && found.indexOf(type) === -1) found.push(type);
    });

    return found.length === 1 ? found[0] : null;
  }

  /*
   * Built with DOM methods rather than an HTML string, so a keyword containing
   * a quote or an angle bracket cannot break the markup or inject anything.
   */
  function buildSuggestionItem(keyword, query, searchUrl) {
    var item = document.createElement('li');
    item.className = 'predictive-search-result__list-item flex';
    item.setAttribute('role', 'option');
    item.setAttribute('aria-selected', 'false');

    var link = document.createElement('a');
    link.className = 'reversed-link inline-flex items-center gap-2';
    link.setAttribute('aria-label', keyword);

    var params = new URLSearchParams();
    params.set('type', 'product');
    params.set('options[prefix]', 'last');
    params.set('q', keyword);

    // A suggestion is a search, so it narrows the same way one typed by hand
    // would. Without this, clicking "klinker badrum" would behave differently
    // from typing it.
    var suggestedType = detectProductType(keyword);
    if (suggestedType) params.set(TYPE_PARAM, suggestedType);

    link.href = searchUrl + '?' + params.toString();

    // Same magnifier the theme uses on its own suggestions column.
    var icon = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    icon.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
    icon.setAttribute('width', '16');
    icon.setAttribute('height', '16');
    icon.setAttribute('viewBox', '0 0 16 16');
    icon.setAttribute('fill', 'none');
    icon.setAttribute('aria-hidden', 'true');
    [
      'M7.25 12.5C10.1495 12.5 12.5 10.1495 12.5 7.25C12.5 4.35051 10.1495 2 7.25 2C4.35051 2 2 4.35051 2 7.25C2 10.1495 4.35051 12.5 7.25 12.5Z',
      'M10.9609 10.9636L13.9985 14.0012',
    ].forEach(function (d) {
      var path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
      path.setAttribute('d', d);
      path.setAttribute('stroke', 'currentColor');
      path.setAttribute('stroke-width', '2');
      path.setAttribute('stroke-linecap', 'round');
      path.setAttribute('stroke-linejoin', 'round');
      icon.appendChild(path);
    });
    link.appendChild(icon);

    // The matched run is bolded, the rest left plain, as the app did.
    var label = document.createElement('span');
    var index = matchIndex(keyword, query);

    if (index === -1 || query.length === 0) {
      label.textContent = keyword;
    } else {
      label.appendChild(document.createTextNode(keyword.slice(0, index)));
      var strong = document.createElement('strong');
      strong.textContent = keyword.slice(index, index + query.length);
      label.appendChild(strong);
      label.appendChild(document.createTextNode(keyword.slice(index + query.length)));
    }

    link.appendChild(label);
    item.appendChild(link);
    return item;
  }

  function buildSuggestions(el, query) {
    var keywords = getKeywords(el);
    if (keywords.length === 0) return null;

    var matches = keywords
      .filter(function (keyword) {
        return matchIndex(keyword, query) !== -1;
      })
      .slice(0, MAX_SUGGESTIONS);

    if (matches.length === 0) return null;

    var wrapper = document.createElement('div');
    wrapper.className =
      'grid gap-3 predictive-search-result search__box-item predictive-search-result--queries';

    var heading = document.createElement('h4');
    heading.className = 'h6 predictive-search-result__heading';
    heading.textContent = el.dataset.suggestionsHeading || 'Förslag';
    wrapper.appendChild(heading);

    var list = document.createElement('ul');
    list.className = 'predictive-search-result__list flex flex-col text-subtext gap-y-2';
    list.setAttribute('role', 'group');

    var searchUrl = el.dataset.searchUrl || '/search';
    matches.forEach(function (keyword) {
      list.appendChild(buildSuggestionItem(keyword, query, searchUrl));
    });

    wrapper.appendChild(list);
    return wrapper;
  }

  /* ------------------------------------------------------------------ *
   * Highlighting
   * ------------------------------------------------------------------ */

  /*
   * Shopify returns styled_text with the match already marked up, but only for
   * its own query suggestions, which this storefront never receives. Collection
   * and page titles arrive as plain text, so the match is marked here to keep
   * every column consistent.
   *
   * Rewrites text nodes only, so an entry's icon and structure are untouched.
   */
  function highlightMatch(link, query) {
    if (!link || query.length === 0) return;
    if (link.querySelector('strong')) return;

    Array.prototype.slice.call(link.childNodes).forEach(function (node) {
      if (node.nodeType !== Node.TEXT_NODE) return;

      var text = node.textContent;
      var index = matchIndex(text, query);
      if (index === -1) return;

      var fragment = document.createDocumentFragment();
      fragment.appendChild(document.createTextNode(text.slice(0, index)));

      var strong = document.createElement('strong');
      strong.textContent = text.slice(index, index + query.length);
      fragment.appendChild(strong);

      fragment.appendChild(document.createTextNode(text.slice(index + query.length)));
      node.parentNode.replaceChild(fragment, node);
    });
  }

  /*
   * Shopify ranks collections its own way, which surfaces vendor collections
   * like "Lhådös kakel" above the plain "Kakel" category. Reordered here so an
   * exact title match comes first, then titles starting with the query, then a
   * match at a word boundary, then anything else. Order within each band is
   * left as Shopify returned it.
   */
  function rankCollections(results, query) {
    var list = results.querySelector('.predictive-search-result--collections ul');
    if (!list || query.length === 0) return;

    var needle = normalise(query);

    var items = Array.prototype.slice.call(list.children).map(function (item, index) {
      var title = normalise((item.textContent || '').trim());
      var score = 4;

      if (title === needle) {
        score = 0;
      } else if (title.indexOf(needle) === 0) {
        score = 1;
      } else if (title.split(/\s+/).some(function (word) { return word.indexOf(needle) === 0; })) {
        score = 2;
      } else if (title.indexOf(needle) !== -1) {
        score = 3;
      }

      return { item: item, score: score, index: index };
    });

    items.sort(function (a, b) {
      return a.score === b.score ? a.index - b.index : a.score - b.score;
    });

    items.forEach(function (entry) {
      list.appendChild(entry.item);
    });
  }

  function highlightLists(results, query) {
    var selectors = [
      '.predictive-search-result--collections a',
      '.predictive-search-result--article a',
    ];

    selectors.forEach(function (selector) {
      Array.prototype.slice.call(results.querySelectorAll(selector)).forEach(function (link) {
        highlightMatch(link, query);
      });
    });
  }

  /* ------------------------------------------------------------------ *
   * See all results
   * ------------------------------------------------------------------ */

  /*
   * The theme renders this button underneath the products. The old panel had it
   * at the foot of the list column, full width. Moved rather than duplicated,
   * so it keeps the form association that makes it submit the search.
   */
  function moveSeeAllButton(results) {
    var button = results.querySelector('.search__results-all');
    var sidebar = results.querySelector('.search__box-others');
    if (!button || !sidebar) return;
    if (button.parentNode === sidebar) return;

    sidebar.appendChild(button);
  }

  /*
   * When Shopify finds nothing at all, the theme renders its empty state
   * instead of the usual two column layout, so there is no sidebar to insert
   * into. The suggestions are most useful in exactly that situation, since they
   * are the only route left out of a dead end, so a minimal sidebar is built
   * here and placed under the "no results" message.
   */
  function injectIntoEmptyState(el, results, query) {
    var block = buildSuggestions(el, query);
    if (!block) return;

    block.classList.add('js-keyword-suggestions');

    var host = document.createElement('div');
    host.className = 'search__box-others js-keyword-suggestions flex flex-col gap-6 lg:gap-8 no-products';
    host.appendChild(block);

    var emptyState = results.querySelector('.empty-state');
    if (emptyState) {
      emptyState.appendChild(host);
    } else {
      results.appendChild(host);
    }
  }

  /*
   * Runs after the theme has swapped in the fetched results. The column is
   * inserted first inside the sidebar so it sits above Kategorier, matching the
   * order the old app used.
   */
  function injectSuggestions(el) {
    var results = el.querySelector('[id^="PredictiveSearchResults-"]');
    if (!results) return;

    Array.prototype.slice
      .call(results.querySelectorAll('.js-keyword-suggestions'))
      .forEach(function (node) {
        if (node.parentNode) node.parentNode.removeChild(node);
      });

    var query = typeof el.getQuery === 'function' ? el.getQuery() : '';
    if (query.length === 0) return;

    var sidebar = results.querySelector('.search__box-others');
    if (!sidebar) {
      injectIntoEmptyState(el, results, query);
      rankCollections(results, query);
      highlightLists(results, query);
      return;
    }

    var block = buildSuggestions(el, query);
    if (block) {
      block.classList.add('js-keyword-suggestions');
      sidebar.insertBefore(block, sidebar.firstChild);
    }

    rankCollections(results, query);
    highlightLists(results, query);
    moveSeeAllButton(results);
  }

  /* ------------------------------------------------------------------ *
   * Patch
   * ------------------------------------------------------------------ */

  function patch() {
    var element = customElements.get('predictive-search');
    if (!element || !element.prototype) return;

    var proto = element.prototype;
    if (proto.__searchEnhanced) return;

    var originalSetupURL = proto.setupURL;
    if (typeof originalSetupURL === 'function') {
      proto.setupURL = function () {
        var url = originalSetupURL.call(this);
        url.searchParams.set('resources[type]', RESOURCE_TYPES);
        url.searchParams.set('resources[options][fields]', SEARCH_FIELDS);
        return url;
      };
    }

    /*
     * Closing the panel leaves the results attribute on the form, and the theme
     * styles the input from that attribute: it keeps the open-state background,
     * which resolves to the header navy once the panel is gone, so the field
     * turns into a dark bar with the clear button still showing. Dropping the
     * attribute on close returns the input to its resting appearance.
     */
    var originalToggle = proto.toggleSearchState;
    if (typeof originalToggle === 'function') {
      proto.toggleSearchState = function (isOpen) {
        originalToggle.call(this, isOpen);
        if (!isOpen) {
          this.removeAttribute('results');
          this.removeAttribute('loading');
          this.removeAttribute('error');
        }
      };
    }

    var originalClear = proto.clear;
    if (typeof originalClear === 'function') {
      proto.clear = function (event) {
        originalClear.call(this, event);
        this.removeAttribute('results');
        this.removeAttribute('loading');
      };
    }

    var originalRender = proto.renderSearchResults;
    if (typeof originalRender === 'function') {
      proto.renderSearchResults = function (responseText) {
        originalRender.call(this, responseText);
        try {
          injectSuggestions(this);
        } catch (error) {
          // Never let a suggestions failure take the search results down.
          console.error('Suggestions column failed to render:', error);
        }
      };
    }

    proto.__searchEnhanced = true;
  }

  /*
   * whenDefined rather than a plain call, so this works whichever order the two
   * scripts load in. Patching the prototype after elements are upgraded is
   * still fine, since both methods run per keystroke rather than at
   * construction.
   */
  if (window.customElements && customElements.whenDefined) {
    customElements.whenDefined('predictive-search').then(patch);
  }

  /* ------------------------------------------------------------------ *
   * Applying the narrowing on submit
   *
   * Done at submit time rather than on the results page, so the page loads
   * already narrowed instead of loading wide and then reloading. The hidden
   * input is rewritten on every submit, so changing the query from "klinker
   * badrum" to "kakel badrum" moves the filter with it.
   * ------------------------------------------------------------------ */
  function narrowOnSubmit(event) {
    var form = event.target;
    if (!form || form.tagName !== 'FORM') return;

    var input = form.querySelector('input[name="q"]');
    if (!input) return;

    var existing = form.querySelector('input[data-type-narrow]');
    if (existing) existing.parentNode.removeChild(existing);

    var type = detectProductType(input.value);
    if (!type) return;

    var hidden = document.createElement('input');
    hidden.type = 'hidden';
    hidden.name = TYPE_PARAM;
    hidden.value = type;
    hidden.setAttribute('data-type-narrow', 'true');
    form.appendChild(hidden);
  }

  // Capture phase and delegated from the document, so this also covers the
  // search form on the results page and any form the theme re-renders.
  document.addEventListener('submit', narrowOnSubmit, true);
})();